import { LANE_COLORS, cssColor } from '../render/colors';
import type { RaceResult } from '../sim/race/types';
import { el, formatTime } from './dom';

function ordinal(place: number): string {
  const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
  return `${place}${suffix}`;
}

export interface ResultsHandlers {
  /** Omit to hide the rerun button (career races are one-shot). */
  onAgain?: () => void;
  onBack: () => void;
  backLabel?: string;
  /** Photo-finish verdict ("won by a nose"). */
  verdict?: string;
}

/** Post-race modal. R races again with a fresh seed; T returns to setup. */
export function showResults(
  root: HTMLElement,
  result: RaceResult,
  handlers: ResultsHandlers,
): () => void {
  const winner = result.placings.find((placing) => placing.timeS !== null);
  const rows = result.placings.map((placing, index) => {
    const swatch = el('span', { class: 'swatch' });
    swatch.style.background = cssColor(LANE_COLORS[placing.lane] ?? 0xffffff);
    const margin =
      placing.timeS !== null && winner && winner.timeS !== null && placing.timeS > winner.timeS
        ? `+${(placing.timeS - winner.timeS).toFixed(2)}`
        : '';
    return el(
      'div',
      { class: placing.lane === 0 ? 'results-row you' : 'results-row' },
      el('span', { class: 'place' }, placing.timeS === null ? '—' : ordinal(index + 1)),
      swatch,
      el('span', { class: 'name' }, placing.name),
      el('span', { class: 'time' }, formatTime(placing.timeS)),
      el('span', { class: 'margin' }, margin),
    );
  });

  const headline = result.abandoned
    ? 'Pulled up.'
    : result.playerPlace === 1
      ? 'You won!'
      : result.playerTimeS === null
        ? 'Did not finish.'
        : `${ordinal(result.playerPlace)} place.`;

  const stats = `${result.taps} taps · blown ${result.exhaustedCount}×${
    handlers.verdict ? ` · ${handlers.verdict}` : ''
  }`;

  const panel = el(
    'div',
    { class: 'panel modal results' },
    el('div', { class: 'modal-title' }, headline),
    el('div', { class: 'results-sub' }, stats),
    ...rows,
    el(
      'div',
      { class: 'modal-foot' },
      ...(handlers.onAgain
        ? [el('button', { class: 'button', onclick: () => handlers.onAgain!() }, 'Race again (R)')]
        : []),
      el(
        'button',
        { class: 'button primary', onclick: () => handlers.onBack() },
        handlers.backLabel ?? 'Back to stats (T)',
      ),
    ),
  );

  const backdrop = el('div', { class: 'modal-backdrop' }, panel);
  root.append(backdrop);
  return () => backdrop.remove();
}
